import React from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveEmendaStatus, type EmendaDTO } from '../../services/emendaService';
import type { InstitutionDTO } from '../../services/institutionService';
import type { PlanoTrabalho } from '../../types/planoTrabalho.types';
import { STATUS_OSC_LABELS, STATUS_OSC_COLORS, type StatusOSC } from '../../types/statusOSC.types';
import { fMoney } from '../../utils/emendaFormatters';
import { useAuth } from '../../context/AuthContext';
import { Permission } from '../../types/user.types';
import Button from '../ui/Button';

type Tone = 'accent' | 'warning' | 'success' | 'muted';

const STRIP_TONE: Record<Tone, string> = {
  accent: 'border-info-line bg-info-soft text-info-fg',
  warning: 'border-warning-line bg-warning-soft text-warning-text',
  success: 'border-success-line bg-success-soft text-success-text',
  muted: 'border-line bg-surface-muted text-fg-muted',
};

// ── Emenda: status → responsável / tom ────────────────────────────────────────
const EMENDA_RESPONSAVEL: Record<string, string> = {
  'recebido': 'Orçamento',
  'em análise de admissibilidade': 'Orçamento',
  'admissibilidade aprovada': 'Secretaria',
  'em análise de demanda': 'Secretaria',
  'análise de demanda aprovada': 'Secretaria / Convênios',
  'em análise documental': 'Jurídico / Convênios',
  'análise documental aprovada': 'Jurídico / Convênios',
  'iniciado': 'Operador',
  'em execução': 'Operador',
  'concluído': '—',
};

const emendaResponsavel = (status: string): string => {
  const key = status.trim().toLocaleLowerCase('pt-BR');
  if (EMENDA_RESPONSAVEL[key] !== undefined) return EMENDA_RESPONSAVEL[key];
  if (key.startsWith('retornada ao orçamento')) return 'Orçamento';
  if (key.startsWith('devolvida') || key === 'devolvido') return 'Câmara / Parlamentar';
  return '—';
};

const emendaTone = (status: string): Tone => {
  const key = status.trim().toLocaleLowerCase('pt-BR');
  if (key.startsWith('retornada ao orçamento')) return 'warning';
  if (key.startsWith('devolvid')) return 'warning';
  if (key === 'concluído') return 'success';
  return 'accent';
};

const emendaLabel = (emenda: EmendaDTO): string => {
  // officialCode já é o código oficial completo (ex.: 001-451-2026); exibir cru,
  // como nas demais telas. Nunca prefixar EM-/exercício (duplicava o ano).
  if (emenda.officialCode) return emenda.officialCode;
  if (emenda.exercicio && emenda.numeroEmenda) return `${emenda.exercicio}-${String(emenda.numeroEmenda).padStart(3, '0')}`;
  if (emenda.id) return `#${emenda.id.slice(0, 8)}`;
  return 'S/N';
};

// ── Instituição: statusOSC → responsável / tom ────────────────────────────────
const INSTITUICAO_RESPONSAVEL: Record<string, string> = {
  EM_CADASTRO: 'Operador',
  DOCUMENTOS_INCOMPLETOS: 'Operador',
  AGUARDANDO_ANALISE: 'Analista',
  EM_ANALISE: 'Analista',
  APROVADO: '—',
  REPROVADO: 'Operador',
  SUSPENSA: 'Analista',
  INATIVA: '—',
};

const instituicaoStatusLabel = (status?: string): string =>
  status ? (STATUS_OSC_LABELS[status as StatusOSC] || status) : 'Não informado';

const instituicaoStatusTone = (status?: string): string =>
  status ? (STATUS_OSC_COLORS[status as StatusOSC] || 'border-line bg-surface-muted text-fg') : 'border-line bg-surface-muted text-fg-muted';

const instituicaoResponsavel = (status?: string): string =>
  status ? (INSTITUICAO_RESPONSAVEL[status] ?? 'Operador') : 'Operador';

const instituicaoTone = (status?: string): Tone => {
  if (status === 'APROVADO') return 'success';
  if (status === 'EM_ANALISE' || status === 'AGUARDANDO_ANALISE') return 'accent';
  return 'warning';
};

// ── Plano de trabalho: etapa / status → responsável / tom ─────────────────────
const PLANO_ETAPA_LABEL: Record<string, string> = {
  ENVIO: 'Envio / assinatura',
  ORCAMENTO: 'Orçamento',
  CONVENIOS_ANALISE: 'Convênios — análise',
  GESTOR_PARCERIA: 'Gestor da parceria',
  COMISSAO_SELECAO: 'Comissão de seleção',
  CONVENIOS_MINUTA: 'Convênios — minuta',
  PROCURADORIA_JURIDICA: 'Procuradoria (PGM)',
  GESTOR_JURIDICO_REVISAO: 'Revisão do Gestor Jurídico',
  CONVENIOS_FORMALIZACAO: 'Convênios — formalização',
  CONCLUIDO: 'Concluído',
};

const PLANO_ETAPA_RESPONSAVEL: Record<string, string> = {
  ENVIO: 'Operador / Instituição',
  ORCAMENTO: 'Orçamento',
  CONVENIOS_ANALISE: 'Convênios',
  GESTOR_PARCERIA: 'Gestor da parceria',
  COMISSAO_SELECAO: 'Comissão de seleção',
  CONVENIOS_MINUTA: 'Convênios',
  PROCURADORIA_JURIDICA: 'Jurídico (PGM)',
  GESTOR_JURIDICO_REVISAO: 'Gestor Jurídico',
  CONVENIOS_FORMALIZACAO: 'Convênios',
  CONCLUIDO: '—',
};

const PLANO_STATUS_LABEL: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  EM_ANALISE: 'Em análise',
  APROVADO: 'Aprovado',
  DEVOLVIDO_CORRECAO: 'Devolvido para correção',
  PENDENTE_RETIFICACAO: 'Pendente de retificação',
  REPROVADO: 'Reprovado',
  CONCLUIDO: 'Concluído',
  FECHADO: 'Fechado',
};

const planoStatusTone = (status?: string): string => {
  switch (status) {
    case 'CONCLUIDO':
    case 'APROVADO':
      return 'border-success-line bg-success-soft text-success-text';
    case 'REPROVADO':
      return 'border-danger-line bg-danger-soft text-danger-text';
    case 'DEVOLVIDO_CORRECAO':
    case 'PENDENTE_RETIFICACAO':
      return 'border-warning-line bg-warning-soft text-warning-text';
    case 'EM_ANALISE':
      return 'border-info-line bg-info-soft text-info-fg';
    case 'AGUARDANDO_ASSINATURA':
      return 'border-orange-200 bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-300';
    default:
      return 'border-line bg-surface-muted text-fg-muted';
  }
};

// ── Etapa atual do processo (primeira fase não concluída) ─────────────────────
// Sequência: Emenda → Instituição → Plano → Execução. O plano é a espinha da
// execução: quando existe, o foco está nele; a instituição só vira "etapa atual"
// quando a emenda já foi encaminhada e a OSC ainda não está regular.
const EMENDA_ENCAMINHADA = new Set([
  'análise de demanda aprovada',
  'em análise documental',
  'análise documental aprovada',
]);

type EtapaAtual = { titulo: string; responsavel: string; tone: Tone; icon: IconName };

const deriveEtapaAtual = (emendaStatus: string, inst?: InstitutionDTO, plano?: PlanoTrabalho): EtapaAtual => {
  if (plano) {
    const st = String(plano.status || '');
    if (st === 'CONCLUIDO' || st === 'FECHADO') return { titulo: 'Execução · Concluído', responsavel: '—', tone: 'success', icon: 'check' };
    if (st === 'APROVADO') return { titulo: 'Plano de trabalho · Aprovado', responsavel: '—', tone: 'success', icon: 'check' };
    if (st === 'EM_ANALISE') {
      const et = plano.etapaAtual || '';
      return { titulo: `Plano · ${PLANO_ETAPA_LABEL[et] || et || 'Em análise'}`, responsavel: PLANO_ETAPA_RESPONSAVEL[et] || 'Convênios', tone: 'accent', icon: 'file' };
    }
    return { titulo: `Plano de trabalho · ${PLANO_STATUS_LABEL[st] || st}`, responsavel: 'Operador / Instituição', tone: 'warning', icon: 'alert' };
  }
  const instAprovada = inst?.statusOSC === 'APROVADO';
  if (inst && !instAprovada && EMENDA_ENCAMINHADA.has(emendaStatus.trim().toLocaleLowerCase('pt-BR'))) {
    return {
      titulo: `Instituição · ${instituicaoStatusLabel(inst.statusOSC)}`,
      responsavel: instituicaoResponsavel(inst.statusOSC),
      tone: instituicaoTone(inst.statusOSC),
      icon: instituicaoTone(inst.statusOSC) === 'accent' ? 'building' : 'alert',
    };
  }
  return { titulo: `Emenda · ${emendaStatus || 'Sem status'}`, responsavel: emendaResponsavel(emendaStatus), tone: emendaTone(emendaStatus), icon: 'gavel' };
};

// ── Ícones (inline, herdam a cor do texto) ────────────────────────────────────
type IconName = 'check' | 'alert' | 'file' | 'fileOff' | 'gavel' | 'building' | 'user' | 'finance' | 'receipt';

const Icon: React.FC<{ name: IconName; className?: string }> = ({ name, className }) => {
  const p: Record<IconName, React.ReactNode> = {
    check: <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />,
    alert: <path fillRule="evenodd" d="M8.3 3.6a2 2 0 013.4 0l6 10.4A2 2 0 0116 17H4a2 2 0 01-1.7-3L8.3 3.6zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-6a1 1 0 00-1 1v3a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />,
    file: <path fillRule="evenodd" d="M4 4a2 2 0 012-2h5.6L16 6.4V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm7 0v3h3l-3-3z" clipRule="evenodd" />,
    fileOff: <path fillRule="evenodd" d="M4 4a2 2 0 012-2h5.6L16 6.4V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm7 0v3h3l-3-3z" clipRule="evenodd" opacity="0.5" />,
    gavel: <path d="M6.3 2.9l2.8 2.8-2.1 2.1-2.8-2.8a1.5 1.5 0 012.1-2.1zm5 1.4l4.4 4.4-2.1 2.1L9.2 6.4l2.1-2.1zM3 15h8v2H3v-2z" />,
    building: <path fillRule="evenodd" d="M4 3a1 1 0 011-1h10a1 1 0 011 1v14h1a1 1 0 110 2H3a1 1 0 110-2h1V3zm3 3h2v2H7V6zm4 0h2v2h-2V6zM7 10h2v2H7v-2zm4 0h2v2h-2v-2z" clipRule="evenodd" />,
    user: <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.5 14.5a1.2 1.2 0 00.4 1.4A10 10 0 0010 18c2.3 0 4.4-.8 6.1-2.1.4-.3.6-.9.4-1.4a7 7 0 00-13 0z" />,
    finance: <path d="M3 16h14v2H3v-2zm1-3h2V8H4v5zm5 0h2V5H9v8zm5 0h2V2h-2v11z" />,
    receipt: <path fillRule="evenodd" d="M5 2a1 1 0 00-1 1v15l2-1 2 1 2-1 2 1 2-1 2 1V3a1 1 0 00-1-1H5zm2 4a1 1 0 000 2h6a1 1 0 100-2H7zm0 4a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />,
  };
  return <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className={className}>{p[name]}</svg>;
};

const Badge: React.FC<{ tone: string; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}>{children}</span>
);

interface Props {
  emendas: EmendaDTO[];
  institutionsById: Map<string, InstitutionDTO>;
  planosByEmendaId: Map<string, PlanoTrabalho>;
}

const EmendaProcessoBoard: React.FC<Props> = ({ emendas, institutionsById, planosByEmendaId }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canViewExecucaoFinanceira = hasPermission(Permission.VIEW_EXECUCAO_FINANCEIRA);
  const canViewPrestacaoContas = hasPermission(Permission.VIEW_PRESTACAO_CONTAS);

  if (emendas.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-surface px-4 py-10 text-center text-sm text-fg-muted">
        Nenhuma emenda encontrada para os filtros aplicados.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {emendas.map((emenda) => {
        const emendaStatus = resolveEmendaStatus(emenda);
        const inst = emenda.institutionId ? institutionsById.get(emenda.institutionId) : undefined;
        const plano = emenda.id ? planosByEmendaId.get(String(emenda.id)) : undefined;
        const etapa = deriveEtapaAtual(emendaStatus, inst, plano);
        const institutionId = inst?.institutionId || inst?.id || emenda.institutionId;
        const abrirInstituicao = () => institutionId && navigate(`/dashboard/cadastro-dados-institucionais?id=${encodeURIComponent(institutionId)}`);
        const abrirPlano = () => plano && navigate(`/dashboard/plano/full/${plano.id}`);

        return (
          <div
            key={String(emenda.id)}
            role="button"
            tabIndex={0}
            onClick={() => emenda.id && navigate(`/dashboard/emendas/${emenda.id}`)}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && emenda.id) navigate(`/dashboard/emendas/${emenda.id}`); }}
            className="cursor-pointer rounded-xl border border-line bg-surface p-4 transition hover:border-line-strong hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-500 dark:focus:ring-slate-500"
          >
            {/* Cabeçalho — emenda */}
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-xs font-semibold text-fg">{emendaLabel(emenda)}</span>
              <span className="text-sm font-semibold text-success-text">{fMoney(emenda.value)}</span>
            </div>
            <div className="mt-0.5 truncate text-xs text-fg-muted">
              {(inst?.nomeFantasia || inst?.razaoSocial || emenda.institutionName || 'Instituição não informada')}
              {emenda.councilorName ? ` · ${emenda.councilorName}` : ''}
            </div>

            {/* Etapa atual */}
            <div className={`mt-3 flex items-center gap-2.5 rounded-lg border px-3 py-2 ${STRIP_TONE[etapa.tone]}`}>
              <Icon name={etapa.icon} className="h-5 w-5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] font-medium uppercase tracking-wide opacity-90">Etapa atual</div>
                <div className="break-words text-[13px] font-semibold leading-tight">
                  {etapa.titulo}
                  {etapa.responsavel && etapa.responsavel !== '—' && (
                    <span className="font-normal opacity-90"> · {etapa.responsavel}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Linha — instituição */}
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Icon name="building" className="h-4 w-4 shrink-0 text-fg-subtle" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-fg">Instituição</div>
                  <div className="truncate text-xs text-fg-subtle">{inst?.nomeFantasia || inst?.razaoSocial || emenda.institutionName || '—'}</div>
                </div>
              </div>
              <Badge tone={instituicaoStatusTone(inst?.statusOSC)}>{instituicaoStatusLabel(inst?.statusOSC)}</Badge>
            </div>

            {/* Linha — plano de trabalho */}
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Icon name={plano ? 'file' : 'fileOff'} className="h-4 w-4 shrink-0 text-fg-subtle" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-fg">Plano de trabalho</div>
                  <div className="truncate text-xs text-fg-subtle">
                    {plano
                      ? `${plano.numeroProtocolo ? plano.numeroProtocolo + ' · ' : ''}${PLANO_ETAPA_LABEL[plano.etapaAtual || ''] || plano.etapaAtual || ''}`
                      : 'Nenhum plano vinculado ainda'}
                  </div>
                </div>
              </div>
              {plano ? (
                <Badge tone={planoStatusTone(plano.status)}>{PLANO_STATUS_LABEL[plano.status || ''] || plano.status || 'Rascunho'}</Badge>
              ) : (
                <Badge tone="border-line bg-surface-muted text-fg-muted">Sem plano</Badge>
              )}
            </div>

            {/* Ações — abrir cada parte direto */}
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3" onClick={(e) => e.stopPropagation()}>
              <Button variant="secondary" size="sm" onClick={() => emenda.id && navigate(`/dashboard/emendas/${emenda.id}`)}>
                <Icon name="gavel" className="h-3.5 w-3.5" /> Emenda
              </Button>
              <Button variant="secondary" size="sm" onClick={abrirInstituicao} disabled={!institutionId}>
                <Icon name="building" className="h-3.5 w-3.5" /> Instituição
              </Button>
              <Button variant="secondary" size="sm" onClick={abrirPlano} disabled={!plano}>
                <Icon name="file" className="h-3.5 w-3.5" /> Plano
              </Button>
              {emenda.id && canViewExecucaoFinanceira && (
                <Button variant="secondary" size="sm" onClick={() => navigate(`/dashboard/execucao-financeira/${emenda.id}`)}>
                  <Icon name="finance" className="h-3.5 w-3.5" /> Execução financeira
                </Button>
              )}
              {canViewPrestacaoContas && (
                <Button variant="secondary" size="sm" onClick={() => plano && navigate(`/dashboard/plano/prestacao-contas?planoId=${plano.id}`)} disabled={!plano}>
                  <Icon name="receipt" className="h-3.5 w-3.5" /> Prestação de contas
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmendaProcessoBoard;
